import { useState } from "react";
import Button from "../../components/common/Button";
import FulfillmentOrderItems from "../fulfillment/FulfillmentOrderItems";
import { useExportFulfillmentOrderItemsMutation } from "../../store/api";
import { downloadBlob } from "../../utils/downloadFile";

const toDateInputValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toStartOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const toEndOfDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

const FulfillmentItemsReport = () => {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [filterKey, setFilterKey] = useState(0);

  const [exportItems, { isLoading: isExporting }] =
    useExportFulfillmentOrderItemsMutation();

  const handleDownload = async () => {
    const result = await exportItems({
      status: status || undefined,
      search: search || undefined,
      from: startDate ? startDate.toISOString() : undefined,
      to: endDate ? endDate.toISOString() : undefined,
    });
    if (!("data" in result)) {
      return;
    }

    const contentDisposition = result?.meta?.response?.headers?.get(
      "content-disposition",
    );
    downloadBlob({
      blob: result.data,
      filename: `fulfillment-items-${new Date().toISOString().slice(0, 10)}.xlsx`,
      contentDisposition,
    });
  };

  return (
    <div className="space-y-2">
      <div className="mb-0 flex items-center gap-2 px-1">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-800 dark:text-slate-100">
          Order Items
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <label className="relative block w-full max-w-xs">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 dark:text-slate-500">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.6-4.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order no / item / station"
              
              className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </label>
          <div className="relative">
            <Button size="sm" variant="secondary" className="h-9 px-3 text-sm" onClick={() => setShowFilters((prev) => !prev)}>
              Filter
            </Button>
            {showFilters && (
              <div className="absolute right-0 top-11 z-20 w-[320px] rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="h-8 w-full rounded border border-slate-200 bg-white px-3 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    >
                      <option value="">All</option>
                      <option value="IN_KITCHEN">IN_KITCHEN</option>
                      <option value="PREPARING">PREPARING</option>
                      <option value="CAPTURED">CAPTURED</option>
                      <option value="PAYMENT_FAILED">PAYMENT_FAILED</option>
                      <option value="COMPLETED">COMPLETED</option>
                      <option value="CANCELLED">CANCELLED</option>
                      <option value="DELIVERED">DELIVERED</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                      From
                    </label>
                    <input
                      type="date"
                      value={toDateInputValue(startDate)}
                      onChange={(e) => setStartDate(toStartOfDay(e.target.value))}
                      className="h-8 w-full rounded border border-slate-200 px-3 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
                      To
                    </label>
                    <input
                      type="date"
                      value={toDateInputValue(endDate)}
                      onChange={(e) => setEndDate(toEndOfDay(e.target.value))}
                      className="h-8 w-full rounded border border-slate-200 px-3 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        const now = new Date();
                        setStatus("");
                        setSearch("");
                        setStartDate(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0));
                        setEndDate(now);
                        setFilterKey((prev) => prev + 1);
                        setShowFilters(false);
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setFilterKey((prev) => prev + 1);
                        setShowFilters(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Button size="sm" className="h-9 px-3 text-sm" onClick={handleDownload} disabled={isExporting}>
            {isExporting ? "Downloading..." : "Download"}
          </Button>
        </div>
      </div>

      <FulfillmentOrderItems
        key={filterKey}
        embedded
        compact
        showFilters={false}
        showDownloadButton={false}
        status={status}
        onStatusChange={setStatus}
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
      />
    </div>
  );
};

export default FulfillmentItemsReport;
